import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import App from './App.tsx'
import './index.css'

// Validate environment configuration
import { validateEnvironment } from '@/utils/envValidation'
validateEnvironment()

const CHUNK_RELOAD_KEY = 'oltu_chunk_reload_once'

function isStaleChunkFailure(reason: unknown): boolean {
  const msg = reason instanceof Error ? reason.message : String(reason ?? '')
  const name = reason instanceof Error ? reason.name : ''
  return (
    name === 'ChunkLoadError' ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('ChunkLoadError')
  )
}

function recoverStaleChunkOnce(): void {
  if (typeof window === 'undefined') return
  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
  } catch {
    return
  }
  window.location.reload()
}

window.addEventListener('unhandledrejection', (event) => {
  if (!isStaleChunkFailure(event.reason)) return
  event.preventDefault()
  recoverStaleChunkOnce()
})

window.addEventListener('error', (event) => {
  const msg = event.message || ''
  if (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('ChunkLoadError')
  ) {
    event.preventDefault()
    recoverStaleChunkOnce()
  }
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: (failureCount, error: any) => {
        if (error?.status === 401 || error?.status === 403) return false
        return failureCount < 2
      },
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter
        future={{
          v7_relativeSplatPath: true,
          // Lazy routes suspend while loading; without this, click navigations are "sync input" and React throws #426.
          v7_startTransition: true,
        }}
      >
        <App />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
            },
            error: {
              duration: 5000,
            },
          }}
        />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
)
